import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { ServiceApiService } from '../../Services/service-api.service';
import { TitleSuggestionService, TitleSuggestion } from '../../Services/title-suggestion.service';
import { Service } from '../../objects/service';

@Component({
  selector: 'app-provider-services',
  templateUrl: './provider-services.component.html',
  styleUrls: ['./provider-services.component.css']
})
export class ProviderServicesComponent implements OnInit, OnDestroy {
  services: Service[] = [];
  serviceForm!: FormGroup;
  editingId: string | null = null;
  isLoading = false;
  isSaving = false;
  showForm = false;

  // Title AI suggestions
  titleSuggestions: TitleSuggestion | null = null;
  isFetchingSuggestions = false;
  suggestionError = false;

  readonly categories = [
    'Electrician', 'Plumbing', 'Carpentry', 'General Contractor',
    'Painting', 'HVAC', 'Roofing', 'Landscaping', 'Flooring',
    'Drywall', 'Masonry', 'Other'
  ];

  private providerId!: number;
  private providerName!: string;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private serviceApi: ServiceApiService,
    private titleSuggestionSvc: TitleSuggestionService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    if (!user) { this.router.navigate(['/auth']); return; }
    this.providerId = Number(user.id);
    this.providerName = `${user.firstName} ${user.lastName}`.trim() || user.username;

    this.serviceForm = this.fb.group({
      title:       ['', Validators.required],
      description: [''],
      category:    ['', Validators.required],
      price:       [0, [Validators.required, Validators.min(0)]],
      duration:    [60, [Validators.required, Validators.min(1)]],
      serviceArea: ['', Validators.required],
      isRecurring: [false],
    });

    this.watchTitleForSuggestions();
    this.loadServices();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private watchTitleForSuggestions(): void {
    this.serviceForm.get('title')!.valueChanges.pipe(
      debounceTime(900),
      distinctUntilChanged(),
      filter((val: string) => !!val && val.trim().length >= 4),
      takeUntil(this.destroy$),
      switchMap((title: string) => {
        const category = this.serviceForm.get('category')?.value || 'General';
        this.titleSuggestions = null;
        this.suggestionError = false;
        this.isFetchingSuggestions = true;
        return this.titleSuggestionSvc.getSuggestions(title, category);
      })
    ).subscribe({
      next: (result) => {
        this.isFetchingSuggestions = false;
        this.titleSuggestions = result.suggestions.length > 0 ? result : null;
      },
      error: () => {
        this.isFetchingSuggestions = false;
        this.suggestionError = true;
      }
    });
  }

  applySuggestion(suggestion: string): void {
    this.serviceForm.get('title')!.setValue(suggestion, { emitEvent: false });
    this.titleSuggestions = null;
  }

  dismissSuggestions(): void {
    this.titleSuggestions = null;
    this.suggestionError = false;
  }

  loadServices(): void {
    this.isLoading = true;
    this.serviceApi.getByProvider(this.providerId).subscribe({
      next: (data) => { this.services = data; this.isLoading = false; },
      error: ()    => { this.services = []; this.isLoading = false; }
    });
  }

  openAddForm(): void {
    this.editingId = null;
    this.titleSuggestions = null;
    this.suggestionError = false;
    this.serviceForm.reset({ isRecurring: false, price: 0, duration: 60 });
    this.showForm = true;
  }

  editService(svc: Service): void {
    this.editingId = svc.id;
    this.titleSuggestions = null;
    this.serviceForm.setValue({
      title:       svc.title,
      description: svc.description,
      category:    svc.category,
      price:       svc.price,
      duration:    svc.duration,
      serviceArea: svc.serviceArea.join(', '),
      isRecurring: svc.isRecurring,
    });
    this.showForm = true;
    window.scrollTo(0, 0);
  }

  cancelEdit(): void {
    this.showForm = false;
    this.editingId = null;
    this.titleSuggestions = null;
    this.suggestionError = false;
    this.serviceForm.reset({ isRecurring: false, price: 0, duration: 60 });
  }

  onSubmit(): void {
    if (this.serviceForm.invalid) return;
    this.isSaving = true;

    const fv = this.serviceForm.value;
    const service: Service = {
      id:           this.editingId ?? '0',
      providerId:   this.providerId,
      providerName: this.providerName,
      title:        fv.title,
      description:  fv.description,
      category:     fv.category,
      price:        fv.price,
      duration:     fv.duration,
      serviceArea:  fv.serviceArea.split(',').map((s: string) => s.trim()).filter(Boolean),
      isRecurring:  fv.isRecurring,
      rating:       0,
      reviewCount:  0,
      images:       [],
      availability: [],
    };

    const call = this.editingId
      ? this.serviceApi.update(this.editingId, service)
      : this.serviceApi.create(service);

    call.subscribe({
      next: (saved) => {
        if (this.editingId) {
          const idx = this.services.findIndex(s => s.id === this.editingId);
          if (idx > -1) this.services[idx] = saved;
        } else {
          this.services = [saved, ...this.services];
        }
        this.cancelEdit();
        this.isSaving = false;
      },
      error: () => { this.isSaving = false; }
    });
  }

  goBack(): void {
    this.router.navigate(['/provider-dashboard']);
  }

  deleteService(svc: Service): void {
    if (!confirm(`Delete "${svc.title}"?`)) return;
    this.serviceApi.delete(svc.id).subscribe({
      next: () => { this.services = this.services.filter(s => s.id !== svc.id); },
      error: () => {}
    });
  }
}
